"use client";

import { Check, Clock } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDismissedChecks } from "@/hooks/useDismissedChecks";
import { useAICheckStore } from "@/stores/aicheck";
import type { AICheckRunResult, FocusedEvidenceTarget } from "@/types/aicheck";
import {
  CheckItemDetailEmpty,
  CheckItemDetailPanel,
} from "./shared/CheckItemDetailPanel";
import { DismissRestoreButton } from "./shared/DismissRestoreButton";
import { MarkdownText } from "./shared/MarkdownText";
import { SourceBadge } from "./shared/SourceBadge";
import { StatusBadge } from "./shared/StatusBadge";
import { scrollElementIntoContainer } from "./shared/scroll-utils";
import { compareByStatusAndImportance } from "./shared/sort-utils";
import { getImportanceColor } from "./shared/style-utils";

interface RemindersViewProps {
  result: AICheckRunResult;
  onReturnToSummary?: (findingIndex: number) => void;
  onProjectDataReferenceClick?: (source: string) => void;
}

interface ReminderItem {
  id: string;
  content: string;
  detail: string;
  completed: boolean;
  score?: number;
  rawAIScore?: number;
  scoreAdjusted?: boolean;
  status: string;
  importance: string;
  rawResult?: Record<string, unknown>;
  source: "ai" | "rule";
}

export function RemindersView({
  result,
  onReturnToSummary,
  onProjectDataReferenceClick,
}: RemindersViewProps) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingScrollCheckId, setPendingScrollCheckId] = useState<
    string | null
  >(null);
  const [pendingEvidenceTarget, setPendingEvidenceTarget] =
    useState<FocusedEvidenceTarget | null>(null);
  const { dismiss, restore, isDismissed, dismissedCount } = useDismissedChecks(
    result.projectId,
  );
  const [hideDismissed, setHideDismissed] = useState(false);
  const focusedCheckId = useAICheckStore((state) => state.focusedCheckId);
  const focusedEvidenceTarget = useAICheckStore(
    (state) => state.focusedEvidenceTarget,
  );
  const highlightCheckId = useAICheckStore((state) => state.highlightCheckId);
  const highlightEvidenceTarget = useAICheckStore(
    (state) => state.highlightEvidenceTarget,
  );
  const summaryReturnTarget = useAICheckStore(
    (state) => state.summaryReturnTarget,
  );
  const setFocusedCheckId = useAICheckStore((state) => state.setFocusedCheckId);
  const setFocusedEvidenceTarget = useAICheckStore(
    (state) => state.setFocusedEvidenceTarget,
  );
  const setHighlightCheckId = useAICheckStore(
    (state) => state.setHighlightCheckId,
  );
  const setHighlightEvidenceTarget = useAICheckStore(
    (state) => state.setHighlightEvidenceTarget,
  );
  const setSummaryReturnTarget = useAICheckStore(
    (state) => state.setSummaryReturnTarget,
  );
  const reminderListRef = useRef<HTMLDivElement>(null);

  const reminderItems = useMemo(() => {
    const items: ReminderItem[] = [];

    for (const r of result.aiResults.filter(
      (r) => r.category === "reminders",
    )) {
      items.push({
        id: r.checkId,
        content: r.checkName,
        detail: r.summary ?? r.error ?? "",
        completed: r.status === "pass",
        score: r.overallScore,
        rawAIScore: r.rawAIScore,
        scoreAdjusted: r.scoreAdjusted,
        status: r.status,
        importance: r.importance ?? "medium",
        rawResult: r.result,
        source: "ai",
      });
    }

    for (const r of result.consistencyResults.filter(
      (r) => r.category === "reminders",
    )) {
      items.push({
        id: r.ruleId,
        content: r.ruleName,
        detail: r.evaluationError
          ? `Evaluation error: ${r.message}`
          : r.message,
        completed: r.evaluationError ? false : r.passed,
        status: r.evaluationError ? "error" : r.passed ? "pass" : r.severity,
        importance: r.importance ?? "medium",
        source: "rule",
      });
    }

    items.sort(compareByStatusAndImportance);
    return items;
  }, [result]);

  const { activeItems, dismissedItems } = useMemo(() => {
    const active: ReminderItem[] = [];
    const dismissed: ReminderItem[] = [];
    for (const item of reminderItems) {
      if (isDismissed(item.id)) dismissed.push(item);
      else active.push(item);
    }
    return { activeItems: active, dismissedItems: dismissed };
  }, [reminderItems, isDismissed]);

  const openCount = activeItems.filter((i) => !i.completed).length;
  const completedCount = activeItems.filter((i) => i.completed).length;
  const selectedItem = reminderItems.find((i) => i.id === selectedId) ?? null;
  const isPendingScrollVisible = useMemo(() => {
    if (!pendingScrollCheckId) return false;
    if (activeItems.some((item) => item.id === pendingScrollCheckId)) {
      return true;
    }
    return (
      !hideDismissed &&
      dismissedItems.some((item) => item.id === pendingScrollCheckId)
    );
  }, [activeItems, dismissedItems, hideDismissed, pendingScrollCheckId]);

  useEffect(() => {
    if (
      !focusedCheckId ||
      !reminderItems.some((item) => item.id === focusedCheckId)
    ) {
      return;
    }

    if (hideDismissed && isDismissed(focusedCheckId)) {
      setHideDismissed(false);
    }

    setSelectedId(focusedCheckId);
    setHighlightCheckId(focusedCheckId);
    setPendingScrollCheckId(focusedCheckId);
    setFocusedCheckId(null);
  }, [
    focusedCheckId,
    hideDismissed,
    isDismissed,
    reminderItems,
    setFocusedCheckId,
    setHighlightCheckId,
  ]);

  useEffect(() => {
    if (!pendingScrollCheckId || !isPendingScrollVisible) return;

    const element = document.getElementById(
      `check-card-${pendingScrollCheckId}`,
    );
    if (!element || !reminderListRef.current) {
      return;
    }

    const rafId = requestAnimationFrame(() => {
      scrollElementIntoContainer(reminderListRef.current, element);
      setPendingScrollCheckId((current) =>
        current === pendingScrollCheckId ? null : current,
      );
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [isPendingScrollVisible, pendingScrollCheckId]);

  useEffect(() => {
    if (
      !focusedEvidenceTarget ||
      !reminderItems.some((item) => item.id === focusedEvidenceTarget.checkId)
    ) {
      return;
    }

    if (hideDismissed && isDismissed(focusedEvidenceTarget.checkId)) {
      setHideDismissed(false);
    }

    setSelectedId(focusedEvidenceTarget.checkId);
    setHighlightCheckId(focusedEvidenceTarget.checkId);
    setHighlightEvidenceTarget(focusedEvidenceTarget);
    setPendingScrollCheckId(focusedEvidenceTarget.checkId);
    setPendingEvidenceTarget(focusedEvidenceTarget);
    setFocusedEvidenceTarget(null);
  }, [
    focusedEvidenceTarget,
    hideDismissed,
    isDismissed,
    reminderItems,
    setFocusedEvidenceTarget,
    setHighlightCheckId,
    setHighlightEvidenceTarget,
  ]);

  useEffect(() => {
    if (
      !highlightCheckId ||
      !reminderItems.some((item) => item.id === highlightCheckId)
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      setHighlightCheckId(null);
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [highlightCheckId, reminderItems, setHighlightCheckId]);

  useEffect(() => {
    if (
      !highlightEvidenceTarget ||
      !reminderItems.some((item) => item.id === highlightEvidenceTarget.checkId)
    ) {
      return;
    }

    const timer = window.setTimeout(() => {
      setHighlightEvidenceTarget(null);
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [highlightEvidenceTarget, reminderItems, setHighlightEvidenceTarget]);

  function handleManualSelect(checkId: string) {
    setSummaryReturnTarget(null);
    setPendingEvidenceTarget(null);
    setHighlightEvidenceTarget(null);
    setSelectedId((current) => (current === checkId ? null : checkId));
  }

  return (
    <div className="animate-in fade-in duration-500 space-y-6">
      {/* Summary */}
      {reminderItems.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm text-blue-600">
              <Clock size={24} />
            </div>
            <div>
              <div className="text-sm text-blue-600 font-medium mb-1">
                Action Items Summary
              </div>
              <div className="text-2xl font-bold text-gray-900">
                {openCount} open
                <span className="text-sm font-normal text-gray-500 ml-2">
                  / {activeItems.length} total
                </span>
              </div>
            </div>
          </div>
          <div className="h-10 w-px bg-blue-200 hidden md:block" />
          <div className="flex items-center gap-4">
            <div>
              <div className="text-sm text-blue-600 font-medium mb-1">
                Completed
              </div>
              <div className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                {completedCount} items
                <span className="text-gray-400 text-sm font-normal">
                  resolved
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* List + Detail panel */}
      {reminderItems.length === 0 ? (
        <div className="flex items-center justify-center min-h-[200px] text-gray-400">
          <p className="text-sm">No reminder items.</p>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6 lg:h-[500px]">
          {/* Left: item list */}
          <div className="flex-1 min-h-0">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-bold text-gray-700">Action Items</h3>
              {dismissedCount > 0 && (
                <button
                  type="button"
                  onClick={() => setHideDismissed((v) => !v)}
                  className="rounded-full px-3 py-1 text-xs font-medium bg-zinc-100 text-zinc-500 hover:bg-zinc-200 transition-colors"
                >
                  {dismissedCount} dismissed — {hideDismissed ? "Show" : "Hide"}
                </button>
              )}
            </div>
            <div
              ref={reminderListRef}
              className="space-y-2 overflow-auto lg:h-[calc(100%-2rem)] pr-1"
            >
              {activeItems.map((item) => (
                <div
                  key={item.id}
                  id={`check-card-${item.id}`}
                  className={`group relative flex items-center gap-4 p-4 rounded-lg border transition-all cursor-pointer w-full text-left ${
                    highlightCheckId === item.id
                      ? "border-amber-300 shadow-md ring-2 ring-amber-300 bg-white"
                      : selectedId === item.id
                        ? "border-blue-300 shadow-md ring-1 ring-blue-200"
                        : item.completed
                          ? "bg-gray-50 border-gray-100 opacity-60"
                          : "bg-white border-gray-200 hover:border-blue-200 hover:shadow-sm"
                  }`}
                >
                  {/* Overlay button for card selection */}
                  <button
                    type="button"
                    className="absolute inset-0 z-0 rounded-lg focus-visible:ring-2 focus-visible:ring-blue-300"
                    onClick={() => handleManualSelect(item.id)}
                    aria-label={item.content}
                  />

                  <div
                    className={`relative z-10 pointer-events-none w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                      item.completed
                        ? "bg-green-500 border-green-500 text-white"
                        : "border-gray-300 bg-gray-100"
                    }`}
                  >
                    {item.completed && <Check size={14} />}
                  </div>

                  <div className="flex-1 min-w-0 relative z-10 pointer-events-none">
                    <div
                      className={`text-sm font-medium ${
                        item.completed
                          ? "text-gray-500 line-through"
                          : "text-gray-900"
                      }`}
                    >
                      {item.content}
                    </div>
                    <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                      <MarkdownText mode="inline">{item.detail}</MarkdownText>
                    </div>
                  </div>

                  <div className="shrink-0 flex flex-col items-end gap-1 relative z-10 pointer-events-none">
                    {item.score != null && (
                      <span className="text-xs font-bold tabular-nums text-gray-600">
                        {item.score}
                        {item.scoreAdjusted && item.rawAIScore != null && (
                          <span className="font-normal text-amber-600 ml-1">
                            ({item.rawAIScore})
                          </span>
                        )}
                      </span>
                    )}
                    <div className="flex items-center gap-2">
                      <SourceBadge source={item.source} />
                      <span
                        className={`text-xs font-medium ${getImportanceColor(item.importance)}`}
                      >
                        {item.importance}
                      </span>
                      <StatusBadge status={item.status as "pass"} />
                      <DismissRestoreButton
                        isDismissed={false}
                        onDismiss={() => dismiss(item.id)}
                        onRestore={() => restore(item.id)}
                        variant="card"
                      />
                    </div>
                  </div>
                </div>
              ))}
              {!hideDismissed && dismissedItems.length > 0 && (
                <div className="opacity-40 space-y-2 mt-4 pt-4 border-t border-dashed border-zinc-300">
                  {dismissedItems.map((item) => (
                    <div
                      key={item.id}
                      id={`check-card-${item.id}`}
                      className={`group relative flex items-center gap-4 p-4 rounded-lg border transition-all cursor-pointer w-full text-left ${
                        highlightCheckId === item.id
                          ? "border-amber-300 shadow-md ring-2 ring-amber-300 bg-white"
                          : selectedId === item.id
                            ? "border-blue-300 shadow-md ring-1 ring-blue-200"
                            : "bg-white border-gray-200 hover:border-blue-200 hover:shadow-sm"
                      }`}
                    >
                      {/* Overlay button for card selection */}
                      <button
                        type="button"
                        className="absolute inset-0 z-0 rounded-lg focus-visible:ring-2 focus-visible:ring-blue-300"
                        onClick={() => handleManualSelect(item.id)}
                        aria-label={item.content}
                      />

                      <div className="relative z-10 pointer-events-none w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 border-zinc-300 bg-zinc-100">
                        {item.completed && (
                          <Check size={14} className="text-zinc-400" />
                        )}
                      </div>

                      <div className="flex-1 min-w-0 relative z-10 pointer-events-none">
                        <div className="text-sm font-medium text-gray-500">
                          {item.content}
                        </div>
                        <div className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {item.detail}
                        </div>
                      </div>

                      <div className="shrink-0 flex items-center gap-2 relative z-10 pointer-events-none">
                        <SourceBadge source={item.source} />
                        <StatusBadge status={item.status as "pass"} />
                        <DismissRestoreButton
                          isDismissed={true}
                          onDismiss={() => dismiss(item.id)}
                          onRestore={() => restore(item.id)}
                          variant="card"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right: detail panel */}
          <div className="lg:w-[420px] xl:w-[540px]">
            {selectedItem ? (
              <CheckItemDetailPanel
                title={selectedItem.content}
                detail={selectedItem.detail}
                status={selectedItem.status}
                importance={selectedItem.importance}
                score={selectedItem.score}
                rawAIScore={selectedItem.rawAIScore}
                scoreAdjusted={selectedItem.scoreAdjusted}
                source={selectedItem.source}
                rawResult={selectedItem.rawResult}
                projectData={result.projectData}
                contactTopics={result.contactTopicsResult?.contactTopics}
                isDismissed={isDismissed(selectedItem.id)}
                onDismiss={() => dismiss(selectedItem.id)}
                onRestore={() => restore(selectedItem.id)}
                onClose={() => setSelectedId(null)}
                headerAction={
                  summaryReturnTarget?.checkId === selectedItem.id ? (
                    <button
                      type="button"
                      onClick={() =>
                        onReturnToSummary?.(summaryReturnTarget.findingIndex)
                      }
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                    >
                      ← Executive Summary
                    </button>
                  ) : null
                }
                evidenceIdPrefix={`check-evidence-${selectedItem.id}`}
                focusedEvidenceIndex={
                  pendingEvidenceTarget?.checkId === selectedItem.id
                    ? pendingEvidenceTarget.evidenceIndex
                    : null
                }
                highlightedEvidenceIndex={
                  highlightEvidenceTarget?.checkId === selectedItem.id
                    ? highlightEvidenceTarget.evidenceIndex
                    : null
                }
                onProjectDataReferenceClick={onProjectDataReferenceClick}
                onFocusedEvidenceHandled={() =>
                  setPendingEvidenceTarget((current) =>
                    current?.checkId === selectedItem.id ? null : current,
                  )
                }
                feedbackContext={{
                  runId: (result as { runId?: string }).runId,
                  projectId:
                    result.projectId != null
                      ? String(result.projectId)
                      : undefined,
                  checkId: selectedItem.id,
                }}
              />
            ) : (
              <CheckItemDetailEmpty />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
